import { useFormik } from "formik";
import { Alert, Form } from "antd";
import AppInput from "../../../components/ui/AppInput";
import AppButton from "../../../components/ui/AppButton";
import PasswordPolicyHint from "../../../components/common/PasswordPolicyHint";
import { acceptInviteSchema, acceptInviteInitialValues } from "./AcceptInviteForm.schema";

/**
 * AcceptInviteForm — set initial password for an invited user.
 *
 * Surfaces the password policy inline (8+ chars, mixed case, digit, special).
 * The shell handles SHA-512 hashing + POST.
 */
export default function AcceptInviteForm({ submitting, errorMessage, onSubmit }) {
  const formik = useFormik({
    initialValues: acceptInviteInitialValues,
    validationSchema: acceptInviteSchema,
    onSubmit: async (values) => {
      await onSubmit(values.password);
    },
  });

  return (
    <Form
      layout="vertical"
      onFinish={formik.handleSubmit}
      requiredMark={false}
      className="mt-6 space-y-4"
    >
      <AppInput
        id="invite-password"
        name="password"
        type="password"
        label="New password"
        labelExtra={<PasswordPolicyHint />}
        required
        size="large"
        autoComplete="new-password"
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        status={formik.touched.password && formik.errors.password ? "error" : undefined}
        error={formik.touched.password ? formik.errors.password : undefined}
        restrictLeadingSpace={false}
        restrictConsecutiveSpaces={false}
      />
      <AppInput
        id="invite-confirm"
        name="confirmPassword"
        type="password"
        label="Confirm password"
        required
        size="large"
        autoComplete="new-password"
        value={formik.values.confirmPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        status={
          formik.touched.confirmPassword && formik.errors.confirmPassword ? "error" : undefined
        }
        error={formik.touched.confirmPassword ? formik.errors.confirmPassword : undefined}
        restrictLeadingSpace={false}
        restrictConsecutiveSpaces={false}
      />

      <p className="text-xs text-mahogany/80">
        Use at least 8 characters with an uppercase letter, a lowercase letter, a digit, and a
        special character.
      </p>

      {errorMessage && <Alert type="error" showIcon message={errorMessage} role="alert" />}

      <AppButton
        htmlType="submit"
        type="primary"
        size="large"
        block
        loading={submitting}
        disabled={submitting}
        label="Set password and sign in"
      />
    </Form>
  );
}
